/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  accessAPI,
  ArchivoPreview,
  downloadFile,
  evidenciasDeInteraccion,
  Sidebar,
  subirArchivosPesadosS3,
  TablaFiltrable,
  useIsMobile,
  tituloBase
} from "../../shared";
import { Titulo } from "../../shared";
import { CampoInformacion } from "../../shared";
import { Formulario } from "../../shared";
import { Boton } from "../../shared";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { fechaCorta } from "../../portal/utilidades";
import { Column } from "primereact/column";

export default function Hallazgo() {
  // Un solo botón "Responder hallazgo" según el dispositivo: en web junto al
  // título; en mobile al final, después de leer el hallazgo y sus
  // interacciones (mismo corte web/mobile que el resto del portal).
  const esMovil = useIsMobile(991);
  const [hallazgo, setHallazgo] = useState(null);
  // Título de la pestaña, como en el admin: el de este registro cuando carga
  useEffect(() => {
    if (hallazgo) document.title = hallazgo.resumen || "Hallazgo";
  }, [hallazgo]);
  useEffect(() => () => (document.title = tituloBase()), []);
  const [loader, setLoader] = useState(true);
  const [formLoader, setFormLoader] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [descargandoEvidencia, setDescargandoEvidencia] = useState(null);
  // Archivos para adjuntar a la respuesta (tipos con "Permite archivos"). El
  // ref es para el callback del Formulario, que guarda la config del montaje.
  const [adjuntos, setAdjuntos] = useState([]);
  const adjuntosRef = useRef([]);
  const inputAdjuntosRef = useRef(null);
  const actualizarAdjuntos = (lista) => {
    adjuntosRef.current = lista;
    setAdjuntos(lista);
  };

  const toast = useRef(null);
  const { hallazgoId } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (hallazgoId) {
      accessAPI(
        "GET",
        `cliente/hallazgo/${hallazgoId}`,
        null,
        (respuesta) => {
          setHallazgo(respuesta);
          setLoader(false);
        },
        (respuesta) => {
          setLoader(false);
          if (toast.current) {
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail:
                respuesta[0]?.msg ||
                "Ocurrió un error al cargar el hallazgo.",
              sticky: true
            });
          }
        }
      );
    }
  }, [hallazgoId]);

  function respuestaEnviada(respuesta) {
    toast.current.show({
      severity: "success",
      summary: "Éxito",
      detail: "Respuesta enviada correctamente",
      life: 3000
    });
    setHallazgo(respuesta);
    actualizarAdjuntos([]);
    setFormLoader(false);
    setSidebarVisible(false);
  }

  function errorAlEnviar(respuesta) {
    toast.current.show({
      severity: "error",
      summary: "Error",
      detail: respuesta?.[0]?.msg || "Ocurrió un error, intente nuevamente más tarde.",
      sticky: true
    });
    setFormLoader(false);
  }

  // Lo que pide la respuesta depende del tipo: el análisis (causa raíz y
  // acciones) o un párrafo. Los archivos suben directo a S3 y este mismo
  // endpoint confirma la subida y guarda la respuesta.
  function enviarInteraccion(contenidoCampos) {
    setFormLoader(true);
    const textos = hallazgo.hallazgoTipo?.requiereAnalisis
      ? {
          causaRaiz: contenidoCampos.causaRaiz,
          accionCorrectiva: contenidoCampos.accionCorrectiva,
          accionPreventiva: contenidoCampos.accionPreventiva
        }
      : { respuesta: contenidoCampos.respuesta };
    const archivos = adjuntosRef.current;
    const endpoint = `cliente/hallazgo/${hallazgoId}/interaccion`;
    if (archivos.length === 0) {
      accessAPI("POST", endpoint, { ...textos, archivos: [] }, respuestaEnviada, errorAlEnviar);
      return;
    }
    subirArchivosPesadosS3({
      presignEndpoint: "cliente/archivo/presign",
      confirmarEndpoint: endpoint,
      archivos,
      datosPresign: { ubicacion: "hallazgos" },
      datosConfirmar: textos
    })
      .then(respuestaEnviada)
      .catch(errorAlEnviar);
  }

  const camposRespuesta = hallazgo?.hallazgoTipo?.requiereAnalisis
    ? [
        {
          nombre: "causaRaiz",
          etiqueta: "Causa raíz",
          tipoitemformularioid: 11,
          requerido: true,
          notas: "Análisis de la razón por la que no se detectó esta desviación a tiempo."
        },
        {
          nombre: "accionCorrectiva",
          etiqueta: "Acción correctiva",
          tipoitemformularioid: 11,
          requerido: true,
          notas: "Acciones inmediatas a tomar para remediar la situación actual."
        },
        {
          nombre: "accionPreventiva",
          etiqueta: "Acción preventiva",
          tipoitemformularioid: 11,
          requerido: true,
          notas: "Acciones a tomar para evitar que esta desviación vuelva a ocurrir en el futuro."
        }
      ]
    : [{ nombre: "respuesta", etiqueta: "Respuesta", tipoitemformularioid: 11, requerido: true }];

  // Selección de archivos como en la solicitud del portal: varios a la vez,
  // con la lista para sacar los que no van
  const adjuntosEl = hallazgo?.hallazgoTipo?.permiteArchivos && (
    <div css={{ marginBottom: "1em" }}>
      <div css={{ fontWeight: 600, marginBottom: "0.5em" }}>Archivos</div>
      <input
        ref={inputAdjuntosRef}
        type="file"
        multiple
        onChange={(e) => {
          const nuevos = Array.from(e.target.files || []);
          actualizarAdjuntos([...adjuntosRef.current, ...nuevos]);
          e.target.value = "";
        }}
        style={{ display: "none" }}
      />
      <Boton transparente callback={() => inputAdjuntosRef.current?.click()} disabled={formLoader}>
        Seleccionar archivos
      </Boton>
      {adjuntos.length > 0 && (
        <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.75rem" }}>
          {adjuntos.map((archivo, indice) => (
            <div
              key={`${archivo.name}-${indice}`}
              css={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                background: "var(--p-superficie-suave)",
                borderRadius: 8,
                padding: "0.5rem 0.75rem"
              }}
            >
              <i className="pi pi-file" css={{ color: "var(--principalAcento)" }} />
              <span css={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>{archivo.name}</span>
              <Boton
                icono
                chico
                rojo
                disabled={formLoader}
                callback={() => actualizarAdjuntos(adjuntosRef.current.filter((_, i) => i !== indice))}
              >
                <i className="pi pi-trash" />
              </Boton>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-center" />
      {loader && (
        <div>
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "2em",
              marginBottom: "1em"
            }}
          >
            <Skeleton width="20rem" height="2rem" />
          </div>
          <Card css={{ marginBottom: "1em" }}>
            <div className="grid">
              {[1, 2, 3, 4].map((i) => (
                <div className="col-12 md:col-6" key={i}>
                  <Skeleton
                    width="8rem"
                    height="0.9rem"
                    css={{ marginBottom: "0.3rem" }}
                  />
                  <Skeleton width="12rem" height="1.1rem" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
      {!loader && hallazgo && (
        <div>
          <Titulo
            urlVolver="/hallazgos"
            subtitulo={hallazgo.actividad?.actividadtipo?.nombre}
            botones={
              !hallazgo.fechaCierre && !esMovil && (
                <Boton callback={() => setSidebarVisible(true)}>
                  Responder hallazgo
                </Boton>
              )
            }
          >
            {hallazgo.resumen || hallazgo.actividad?.proceso?.nombrefantasia}
          </Titulo>

          {/* Detalles del hallazgo */}
          <Card css={{ marginBottom: "1em" }}>
            <Titulo>
              <i
                className="pi pi-flag"
                css={{ marginRight: "0.5em" }}
              ></i>
              Detalles del hallazgo
            </Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Tipo">
                {hallazgo.hallazgoTipo?.nombre}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Proceso">
                {[hallazgo.actividad?.proceso?.nombre, hallazgo.actividad?.proceso?.nombrefantasia].filter(Boolean).join(" · ")}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Actividad">
                {hallazgo.actividad?.actividadtipo?.nombre}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha de creación">
                {fechaCorta(hallazgo.fechaCreacion)}
              </CampoInformacion>
              {hallazgo.fechaCierre && (
                <CampoInformacion nombreCampo="Fecha de cierre">
                  {fechaCorta(hallazgo.fechaCierre)}
                </CampoInformacion>
              )}
            </div>
            <Divider />
            <div className="grid">
              <CampoInformacion nombreCampo="Texto" anchoCompleto>
                {hallazgo.texto}
              </CampoInformacion>
            </div>
          </Card>

          {/* Interacciones existentes */}
          {hallazgo.interacciones?.length > 0 && (
            <Card css={{ marginBottom: "1em" }}>
              <Titulo>
                <i
                  className="pi pi-comments"
                  css={{ marginRight: "0.5em" }}
                ></i>
                Interacciones
              </Titulo>
              {hallazgo.interacciones.map((interaccion, idx) => (
                <div key={idx}>
                  {idx > 0 && <Divider />}
                  <div
                    css={{
                      padding: "0.75em",
                      borderRadius: "6px",
                      backgroundColor: interaccion.cierre ? "var(--verdeLight)" : "var(--p-superficie-suave)",
                      border: interaccion.cierre ? "1px solid var(--verde)" : "1px solid var(--p-borde)"
                    }}
                  >
                    <div
                      css={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "0.75em"
                      }}
                    >
                      <span css={{ fontWeight: "bold", fontSize: "0.95em", display: "flex", alignItems: "center", gap: "0.4em" }}>
                        {interaccion.cierre && (
                          <i className="pi pi-check-circle" css={{ color: "var(--verde)" }}></i>
                        )}
                        {interaccion.usuario}
                      </span>
                      <div
                        css={{
                          display: "flex",
                          gap: "0.5em",
                          alignItems: "center"
                        }}
                      >
                        {interaccion.colaborador && (
                          <Tag
                            value="Colaborador"
                            severity="info"
                            css={{ fontSize: "0.7em" }}
                          />
                        )}
                        {interaccion.cliente && (
                          <Tag
                            value="Cliente"
                            severity="success"
                            css={{ fontSize: "0.7em" }}
                          />
                        )}
                        {interaccion.admin && (
                          <Tag
                            value="Admin"
                            severity="warning"
                            css={{ fontSize: "0.7em" }}
                          />
                        )}
                        <span css={{ fontSize: "0.85em", color: "var(--p-texto-2)" }}>
                          {fechaCorta(interaccion.fecha)}
                        </span>
                      </div>
                    </div>
                    {interaccion.textos?.map((texto, tIdx) => (
                      <div key={tIdx} css={{ marginBottom: "0.5em" }}>
                        <span
                          css={{
                            fontSize: "0.85em",
                            color: "var(--p-texto-2)",
                            fontWeight: "600"
                          }}
                        >
                          {texto.nombre}:
                        </span>
                        <p
                          css={{
                            margin: "0.2em 0 0 0",
                            fontSize: "0.95em",
                            whiteSpace: "pre-wrap"
                          }}
                        >
                          {texto.contenido}
                        </p>
                      </div>
                    ))}
                    {/* Archivos de la interacción: vista previa y descarga */}
                    {evidenciasDeInteraccion(interaccion).length > 0 && (
                      <TablaFiltrable
                        conFiltros={false}
                        // La key fuerza a redibujar el loader del botón de descarga
                        key={`evidencias-${idx}-${descargandoEvidencia}`}
                        value={evidenciasDeInteraccion(interaccion)}
                        dataKey="archivoId"
                        size="small"
                        css={{ marginTop: "0.75em" }}
                      >
                        <Column
                          header="Archivos"
                          body={(evidencia) => <ArchivoPreview nombre={evidencia.nombre} hash={evidencia.hash} />}
                        />
                        <Column
                          body={(evidencia) => (
                            <Boton
                              icono
                              chico
                              loader={descargandoEvidencia === evidencia.archivoId}
                              callback={() => {
                                setDescargandoEvidencia(evidencia.archivoId);
                                downloadFile(
                                  `cliente/hallazgo/${hallazgoId}/evidencia/${evidencia.archivoId}`,
                                  evidencia.nombre,
                                  () => setDescargandoEvidencia(null),
                                  () => setDescargandoEvidencia(null)
                                );
                              }}
                            >
                              <i className="pi pi-download" />
                            </Boton>
                          )}
                          style={{ width: "4rem", textAlign: "center" }}
                        />
                      </TablaFiltrable>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          )}

          {/* Botón inferior para responder (solo mobile, a todo el ancho) */}
          {!hallazgo.fechaCierre && esMovil && (
            <div css={{ marginTop: "1.5em", "& .p-button": { width: "100%" } }}>
              <Boton callback={() => setSidebarVisible(true)} ancho>
                Responder hallazgo
              </Boton>
            </div>
          )}

          {/* Sidebar con formulario de respuesta */}
          <Sidebar
            visible={sidebarVisible}
            onHide={() => setSidebarVisible(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Responder hallazgo</Titulo>
            <Formulario
              // Formulario nuevo por hallazgo: la config se toma al montar
              key={hallazgo.id}
              config={{
                secciones: [{ campos: camposRespuesta }],
                botones: [
                  {
                    etiqueta: "Enviar respuesta",
                    callback: enviarInteraccion,
                    validar: true
                  }
                ]
              }}
              loader={formLoader}
              contenidoExtra={adjuntosEl}
            />
          </Sidebar>
        </div>
      )}
    </>
  );
}
